import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Modal,
  TextInput,
  Button,
  Group,
  Stack,
  Text,
  Menu,
} from "@mantine/core";
import {
  IconDots,
  IconFileText,
  IconFileZip,
  IconUpload,
} from "@tabler/icons-react";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { getApiEndpoint } from "../../config/config";

// E-sign statuses as iOS labels them
const SIGNED_STATUS = {
  completed: "Signed",
  signed: "Signed",
  complete: "Signed",
  sent: "Sent",
  in_progress: "In Progress",
  draft: "Draft",
  voided: "Voided",
  expired: "Expired",
};

const statusLabel = status => {
  const key = (status || "").toLowerCase();
  if (SIGNED_STATUS[key]) return SIGNED_STATUS[key];
  return key ? key.charAt(0).toUpperCase() + key.slice(1) : "Unknown";
};

const formatDate = iso => {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const isZip = name => /\.zip$/i.test(name || "");

/// The Pay tab's "Documents · N": the rep's signed e-sign documents, then the
/// files uploaded to them, from GET /v1/rep/:rep_id/documents. Uploads take
/// PDFs and zips; uploaded files can be renamed or deleted.
function RepDocumentsSection({ repId }) {
  const { getVerifiedJWT } = useAuth();
  const { showError } = useToast();

  const [docs, setDocs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  // A single zip is named before it uploads
  const [pendingZip, setPendingZip] = useState(null);
  const [zipLabel, setZipLabel] = useState("");
  const [renaming, setRenaming] = useState(null);
  const [renameLabel, setRenameLabel] = useState("");
  const [deleting, setDeleting] = useState(null);
  const fileInput = useRef(null);

  const request = useCallback(
    async (path, options = {}) => {
      const jwt = await getVerifiedJWT();
      if (!jwt) throw new Error("Authentication failed");
      return fetch(getApiEndpoint(path), {
        ...options,
        headers: { Authorization: `Bearer ${jwt}`, ...options.headers },
      });
    },
    [getVerifiedJWT]
  );

  const load = useCallback(async () => {
    try {
      const response = await request(`/v1/rep/${repId}/documents`);
      if (!response.ok) throw new Error();
      const data = (await response.json()).data || {};
      setDocs([
        ...(data.signed_documents || []).map(d => ({
          key: `signed-${d.id}`,
          title: d.document_name || "Document",
          status: statusLabel(d.status),
          url: d.completed_pdf_url || d.preview_pdf_url,
          date: d.completed_at || d.created_at,
          signed: true,
        })),
        ...(data.uploaded_files || []).map(f => ({
          key: `upload-${f.id}`,
          id: f.id,
          title: f.label || f.file_name || "Document",
          fileName: f.file_name,
          status: "Uploaded",
          url: f.s3_url,
          date: f.created_at,
          signed: false,
        })),
      ]);
    } catch {
      setDocs(prev => prev || []);
      showError("Couldn't load documents. Please try again.");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [repId, request]);

  useEffect(() => {
    load();
  }, [load]);

  const upload = async (files, labelFor) => {
    setUploading(true);
    const failed = [];
    for (const file of files) {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("label", labelFor(file));
      try {
        const response = await request(`/v1/rep/${repId}/documents/upload`, {
          method: "POST",
          body: formData,
        });
        if (!response.ok) failed.push(file.name);
      } catch {
        failed.push(file.name);
      }
    }
    setUploading(false);
    if (failed.length === files.length && files.length === 1) {
      showError("Upload failed. Please try again.");
    } else if (failed.length) {
      showError(`Some files didn't upload: ${failed.join(", ")}`);
    }
    load();
  };

  const handleFiles = event => {
    const files = [...(event.target.files || [])];
    event.target.value = "";
    if (files.length === 0) return;
    if (files.length === 1 && isZip(files[0].name)) {
      setPendingZip(files[0]);
      setZipLabel(files[0].name.replace(/\.zip$/i, ""));
      return;
    }
    upload(files, file => file.name);
  };

  const rename = async () => {
    const label = renameLabel.trim();
    if (!renaming || !label) return;
    try {
      const response = await request(
        `/v1/rep/${repId}/documents/attachments/${renaming.id}`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ label }),
        }
      );
      if (!response.ok) throw new Error();
      setRenaming(null);
      load();
    } catch {
      showError("Couldn't rename that document. Please try again.");
    }
  };

  const remove = async () => {
    if (!deleting) return;
    try {
      const response = await request(
        `/v1/rep/${repId}/documents/attachments/${deleting.id}`,
        { method: "DELETE" }
      );
      if (!response.ok) throw new Error();
      setDeleting(null);
      load();
    } catch {
      showError("Couldn't delete that document. Please try again.");
    }
  };

  return (
    <section className="rep-profile-section">
      <div className="rep-profile-section-head">
        <span className="rep-profile-section-title">
          Documents · {docs?.length ?? 0}
        </span>
        <button
          type="button"
          className="rep-profile-link"
          onClick={() => fileInput.current?.click()}
          disabled={uploading}
        >
          <IconUpload size={14} stroke={2.25} />
          {uploading ? "Uploading..." : "Upload"}
        </button>
        <input
          ref={fileInput}
          type="file"
          accept=".pdf,.zip,application/pdf,application/zip"
          multiple
          hidden
          onChange={handleFiles}
        />
      </div>

      {loading && !docs ? (
        <div className="rep-profile-empty">Loading documents...</div>
      ) : docs.length === 0 ? (
        <div className="rep-profile-empty">No documents yet</div>
      ) : (
        <div className="rep-profile-docs">
          {docs.map(doc => (
            <div key={doc.key} className="rep-profile-doc">
              <a
                className="rep-profile-doc-main"
                href={doc.url || undefined}
                target="_blank"
                rel="noopener noreferrer"
                onClick={event => {
                  if (!doc.url) {
                    event.preventDefault();
                    showError("This document isn't ready to view yet.");
                  }
                }}
              >
                <span className="rep-profile-doc-icon" aria-hidden="true">
                  {isZip(doc.fileName) ? (
                    <IconFileZip size={26} stroke={1.5} />
                  ) : (
                    <IconFileText size={26} stroke={1.5} />
                  )}
                </span>
                <span
                  className={`rep-profile-doc-status is-${doc.status
                    .toLowerCase()
                    .replace(/\s+/g, "-")}`}
                >
                  {doc.status}
                </span>
                <span className="rep-profile-doc-title">{doc.title}</span>
                <span className="rep-profile-doc-date">
                  {formatDate(doc.date)}
                </span>
              </a>
              {!doc.signed && (
                <Menu position="bottom-end" withinPortal>
                  <Menu.Target>
                    <button
                      type="button"
                      className="rep-profile-doc-more"
                      aria-label={`More for ${doc.title}`}
                    >
                      <IconDots size={16} />
                    </button>
                  </Menu.Target>
                  <Menu.Dropdown>
                    <Menu.Item
                      onClick={() => {
                        setRenaming(doc);
                        setRenameLabel(doc.title);
                      }}
                    >
                      Rename
                    </Menu.Item>
                    <Menu.Item color="red" onClick={() => setDeleting(doc)}>
                      Delete
                    </Menu.Item>
                  </Menu.Dropdown>
                </Menu>
              )}
            </div>
          ))}
        </div>
      )}

      <Modal
        opened={Boolean(pendingZip)}
        onClose={() => setPendingZip(null)}
        title="Name this document"
        centered
        size="sm"
      >
        <Stack gap="md">
          <TextInput
            label="Document name"
            description="This is the name shown in the documents list."
            value={zipLabel}
            onChange={e => setZipLabel(e.currentTarget.value)}
            data-autofocus
          />
          <Group justify="flex-end">
            <Button variant="outline" onClick={() => setPendingZip(null)}>
              Cancel
            </Button>
            <Button
              disabled={!zipLabel.trim()}
              onClick={() => {
                const file = pendingZip;
                const label = zipLabel.trim();
                setPendingZip(null);
                upload([file], () => label);
              }}
            >
              Upload
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={Boolean(renaming)}
        onClose={() => setRenaming(null)}
        title="Rename document"
        centered
        size="sm"
      >
        <Stack gap="md">
          <TextInput
            value={renameLabel}
            onChange={e => setRenameLabel(e.currentTarget.value)}
            onKeyDown={e => e.key === "Enter" && rename()}
            data-autofocus
          />
          <Group justify="flex-end">
            <Button variant="outline" onClick={() => setRenaming(null)}>
              Cancel
            </Button>
            <Button disabled={!renameLabel.trim()} onClick={rename}>
              Save
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={Boolean(deleting)}
        onClose={() => setDeleting(null)}
        title={deleting ? `Delete ${deleting.title}?` : ""}
        centered
        size="sm"
      >
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            This removes the uploaded file for everyone. It can't be undone.
          </Text>
          <Group justify="flex-end">
            <Button variant="outline" onClick={() => setDeleting(null)}>
              Cancel
            </Button>
            <Button color="red" onClick={remove}>
              Delete
            </Button>
          </Group>
        </Stack>
      </Modal>
    </section>
  );
}

export default RepDocumentsSection;
